"use client";

import { useEffect, useRef, useState, useTransition } from "react";
import { Send, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { submitContactForm, type ContactFormState } from "./actions";
import { CONTACT_CATEGORIES } from "./constants";

const COOLDOWN_SECONDS = 30;

function useCooldown(seconds: number) {
  const [remaining, setRemaining] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  const start = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    setRemaining(seconds);
    timerRef.current = setInterval(() => {
      setRemaining((prev) => {
        if (prev <= 1) {
          if (timerRef.current) clearInterval(timerRef.current);
          timerRef.current = null;
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  return { remaining, start } as const;
}

export function ContactForm() {
  const [state, setState] = useState<ContactFormState | null>(null);
  const [isPending, startTransition] = useTransition();
  const formRef = useRef<HTMLFormElement>(null);
  const { remaining: cooldown, start: startCooldown } = useCooldown(COOLDOWN_SECONDS);

  const isDisabled = isPending || cooldown > 0;

  function handleSubmit(formData: FormData) {
    startTransition(async () => {
      const result = await submitContactForm(null, formData);
      setState(result);
      if (result.success) {
        startCooldown();
        formRef.current?.reset();
      }
    });
  }

  return (
    <div className="space-y-4">
      {/* Success message */}
      {state?.success && (
        <div className="flex items-start gap-3 rounded-2xl border border-success-border bg-success-soft p-4 text-sm text-success-foreground">
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <p className="font-semibold">Message sent</p>
            <p className="mt-1 text-success-foreground/80">
              Thanks! We&apos;ll get back to you within 24 hours.
            </p>
          </div>
        </div>
      )}

      {/* Error message */}
      {state && !state.success && state.error && (
        <div className="flex items-start gap-3 rounded-2xl border border-destructive-border bg-destructive-soft p-4 text-sm text-destructive-foreground">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <p>{state.error}</p>
            <p className="mt-1 text-destructive-foreground/80">
              Please try again or email us directly at support@dobbix.io.
            </p>
          </div>
        </div>
      )}

      <form ref={formRef} action={handleSubmit} className="space-y-4">
        <div className="space-y-1.5">
          <label htmlFor="contact-name" className="block text-sm font-medium text-foreground">
            Name
          </label>
          <Input
            id="contact-name"
            name="name"
            required
            placeholder="Your name"
            disabled={isDisabled}
            autoComplete="name"
          />
        </div>

        <div className="space-y-1.5">
          <label htmlFor="contact-email" className="block text-sm font-medium text-foreground">
            Email
          </label>
          <Input
            id="contact-email"
            name="email"
            type="email"
            required
            placeholder="you@example.com"
            disabled={isDisabled}
            autoComplete="email"
          />
        </div>

        <div className="space-y-1.5">
          <label htmlFor="contact-category" className="block text-sm font-medium text-foreground">
            Category
          </label>
          <select
            id="contact-category"
            name="category"
            required
            disabled={isDisabled}
            defaultValue=""
            className="flex h-9 w-full rounded-full border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:border-ring focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50"
          >
            <option value="" disabled>
              Select a category
            </option>
            {CONTACT_CATEGORIES.map((cat) => (
              <option key={cat.value} value={cat.value}>
                {cat.label}
              </option>
            ))}
          </select>
        </div>

        <div className="space-y-1.5">
          <label htmlFor="contact-message" className="block text-sm font-medium text-foreground">
            Message
          </label>
          <textarea
            id="contact-message"
            name="message"
            required
            rows={5}
            maxLength={5000}
            placeholder="How can we help?"
            disabled={isDisabled}
            className="flex w-full rounded-2xl border border-input bg-transparent px-3 py-2 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:border-ring focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50"
          />
        </div>

        <Button type="submit" disabled={isDisabled} className="w-full sm:w-auto">
          {isPending ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Sending...
            </>
          ) : cooldown > 0 ? (
            `Wait ${cooldown}s`
          ) : (
            <>
              <Send className="mr-2 h-4 w-4" />
              Send message
            </>
          )}
        </Button>
      </form>
    </div>
  );
}
